// client/src/components/pages/ErrorDetection/ErrorRuleModal.tsx (converted from .js, WBE
// batch ST-00223)
/**
 * Error Rule Modal
 * Create or edit an error detection rule: SQL query, severity thresholds, the daily run
 * time (SAST) and alerting, plus sample query templates.
 *
 * The rule editor is how an admin corrects a stored rule (audit WBE-6: change its SQL or its
 * name; stored rules are never rewritten by a migration). Rule SQL runs in the server's
 * read-only sandbox on PostgreSQL, so the templates are PostgreSQL against
 * lexi_bridge_data_view (they used to be T-SQL against LexiBridgeDataView and failed).
 */

import { useState, type FormEvent } from 'react';
import {
  X,
  Plus,
  Code2,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  FileCode,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import api from '../../../services/api';
import ThemedSelect from '../../common/ThemedSelect';
import ThemedTimePicker from '../../common/ThemedTimePicker';
import type {
  ErrorDetectionResult,
  ErrorDetectionRule,
  SeverityThresholds,
} from '../../../types/clientPages/errorDetection';
import { sampleQueries, type SampleQuery } from './ruleTemplates';

type ThresholdLevel = keyof SeverityThresholds;

interface RuleForm {
  ruleName: string;
  description: string;
  sqlQuery: string;
  severityThresholds: Required<SeverityThresholds>;
  streakDaysToAlert: number | string;
  minAlertSeverity: string;
  scheduleTime: string;
}

interface ErrorRuleModalProps {
  onClose: () => void;
  onSave: () => void;
  /** The full rule (from GET /rules, so with its SQL) when editing; null to create. */
  editRule?: ErrorDetectionRule | ErrorDetectionResult | null;
}

/** An axios error as far as this modal reads it. */
interface ApiError {
  response?: { data?: { error?: string } };
}

const DEFAULT_THRESHOLDS: Required<SeverityThresholds> = { warning: 2, critical: 4, armageddon: 5 };

/* ─── Main Modal ─────────────────────────────── */
const ErrorRuleModal = ({ onClose, onSave, editRule = null }: ErrorRuleModalProps) => {
  const isEditing = !!editRule;
  const [formData, setFormData] = useState<RuleForm>({
    ruleName: editRule?.rule_name || '',
    description: editRule?.description || '',
    sqlQuery: (editRule && 'sql_query' in editRule && editRule.sql_query) || '',
    severityThresholds: { ...DEFAULT_THRESHOLDS, ...(editRule?.severity_thresholds || {}) },
    streakDaysToAlert: editRule?.streak_days_to_alert ?? 2,
    minAlertSeverity: editRule?.min_alert_severity || 'warning',
    // A rule's run time is SAST (migration 045 moved stored times to the time they ran).
    scheduleTime: editRule?.schedule_time || '06:00',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);

  const handleInputChange = <K extends keyof RuleForm>(field: K, value: RuleForm[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const handleThresholdChange = (level: ThresholdLevel, value: string) => {
    setFormData((prev) => ({
      ...prev,
      severityThresholds: {
        ...prev.severityThresholds,
        [level]: parseInt(value) || 0,
      },
    }));
    if (error) setError(null);
  };

  const loadTemplate = (sample: SampleQuery) => {
    setFormData((prev) => ({
      ...prev,
      ruleName: sample.name,
      description: sample.description,
      sqlQuery: sample.query,
    }));
    setShowTemplates(false);
    setError(null);
  };

  const validate = () => {
    if (!formData.ruleName.trim()) {
      setError('Rule name is required');
      return false;
    }
    if (!formData.sqlQuery.trim()) {
      setError('SQL query is required');
      return false;
    }
    const { warning, critical, armageddon } = formData.severityThresholds;
    if (warning < 1 || critical < 1 || armageddon < 1) {
      setError('All thresholds must be at least 1');
      return false;
    }
    if (warning >= critical || critical >= armageddon) {
      setError('Thresholds must be ascending: Warning < Critical < Armageddon');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSaving(true);
      setError(null);
      const payload = {
        ruleName: formData.ruleName.trim(),
        description: formData.description.trim(),
        sqlQuery: formData.sqlQuery.trim(),
        severityThresholds: formData.severityThresholds,
        streakDaysToAlert: parseInt(String(formData.streakDaysToAlert)) || 2,
        minAlertSeverity: formData.minAlertSeverity,
        scheduleTime: formData.scheduleTime,
      };
      if (editRule) {
        await api.errorDetection.updateRule(editRule.rule_id, payload);
      } else {
        await api.errorDetection.createRule(payload);
      }
      onSave();
    } catch (err) {
      console.error('Error saving rule:', err);
      setError(
        (err as ApiError).response?.data?.error ||
          (isEditing ? 'Failed to save rule.' : 'Failed to create rule.') + ' Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary-500">
              <Plus className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2
                className="text-lg font-bold text-slate-900"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {isEditing ? 'Edit Error Detection Rule' : 'New Error Detection Rule'}
              </h2>
              <p className="text-xs text-slate-500">
                {isEditing
                  ? 'Update the rule configuration'
                  : 'Define a SQL query to monitor data quality'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-5">
            {/* Error message */}
            {error && (
              <div className="flex items-center gap-2.5 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 animate-fadeIn">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            {/* Rule Name */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                Rule Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.ruleName}
                onChange={(e) => handleInputChange('ruleName', e.target.value)}
                placeholder="e.g., Duplicate Ticket Numbers"
                className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="What does this rule check for?"
                rows={2}
                className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all resize-none"
              />
            </div>

            {/* SQL Query */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                  <Code2 className="w-3.5 h-3.5" />
                  SQL Query <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowTemplates(!showTemplates)}
                  className="inline-flex items-center gap-1 text-xs text-primary-600 font-medium hover:text-primary-700 transition-colors"
                >
                  <FileCode className="w-3.5 h-3.5" />
                  Templates
                  {showTemplates ? (
                    <ChevronUp className="w-3 h-3" />
                  ) : (
                    <ChevronDown className="w-3 h-3" />
                  )}
                </button>
              </div>

              {/* Templates panel */}
              {showTemplates && (
                <div className="mb-3 grid grid-cols-1 sm:grid-cols-2 gap-2 animate-fadeIn">
                  {sampleQueries.map((sample, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => loadTemplate(sample)}
                      className="text-left p-3 rounded-lg border border-slate-200 hover:border-primary-300 hover:bg-primary-50/50 transition-all group"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-bold text-slate-400 font-mono">
                          {sample.icon}
                        </span>
                        <span className="text-xs font-semibold text-slate-800 group-hover:text-primary-700 transition-colors">
                          {sample.name}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500">{sample.description}</p>
                    </button>
                  ))}
                </div>
              )}

              <textarea
                value={formData.sqlQuery}
                onChange={(e) => handleInputChange('sqlQuery', e.target.value)}
                placeholder="SELECT ... FROM lexi_bridge_data_view WHERE ..."
                rows={6}
                className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all resize-none"
                style={{
                  fontFamily: 'IBM Plex Mono, monospace',
                  fontSize: '12px',
                  lineHeight: '1.6',
                }}
              />
              <p className="text-[10px] text-slate-400 mt-1">
                PostgreSQL. Returned rows = errors detected. Read lexi_bridge_data_view so
                amendments count.
              </p>
            </div>

            {/* Severity Thresholds */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">
                Severity Thresholds
              </label>
              <div className="grid grid-cols-3 gap-3">
                {/* Warning */}
                <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                  <label className="block text-[10px] font-semibold text-amber-600 uppercase tracking-widest mb-1.5">
                    Warning
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.severityThresholds.warning}
                    onChange={(e) => handleThresholdChange('warning', e.target.value)}
                    className="w-full px-2.5 py-2 rounded-md border border-amber-200 text-center text-sm font-bold text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all bg-surface"
                    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  />
                  <p className="text-[10px] text-amber-600/70 mt-1 text-center">errors</p>
                </div>

                {/* Critical */}
                <div className="rounded-lg border border-orange-200 bg-orange-50/50 p-3">
                  <label className="block text-[10px] font-semibold text-orange-600 uppercase tracking-widest mb-1.5">
                    Critical
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.severityThresholds.critical}
                    onChange={(e) => handleThresholdChange('critical', e.target.value)}
                    className="w-full px-2.5 py-2 rounded-md border border-orange-200 text-center text-sm font-bold text-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 transition-all bg-surface"
                    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  />
                  <p className="text-[10px] text-orange-600/70 mt-1 text-center">errors</p>
                </div>

                {/* Armageddon */}
                <div className="rounded-lg border border-red-200 bg-red-50/50 p-3">
                  <label className="block text-[10px] font-semibold text-red-600 uppercase tracking-widest mb-1.5">
                    Armageddon
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.severityThresholds.armageddon}
                    onChange={(e) => handleThresholdChange('armageddon', e.target.value)}
                    className="w-full px-2.5 py-2 rounded-md border border-red-200 text-center text-sm font-bold text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-400 transition-all bg-surface"
                    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  />
                  <p className="text-[10px] text-red-600/70 mt-1 text-center">errors</p>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 mt-2 text-center">
                Warning &lt; Critical &lt; Armageddon (ascending order)
              </p>
            </div>

            {/* Schedule & Alerting */}
            <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">
                Schedule &amp; Alerting
              </label>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-1.5">
                    Daily Run Time (SAST)
                  </label>
                  <ThemedTimePicker
                    value={formData.scheduleTime}
                    label="Daily run time (SAST)"
                    onChange={(e) => handleInputChange('scheduleTime', e.target.value)}
                  />
                  <p className="text-[10px] text-slate-400 mt-1">Every day, South African time</p>
                </div>

                <div>
                  <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-1.5">
                    Alert After
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.streakDaysToAlert}
                    onChange={(e) => handleInputChange('streakDaysToAlert', e.target.value)}
                    className="w-full px-2.5 py-2 rounded-md border border-slate-200 text-center text-sm font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 bg-surface"
                    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  />
                  <p className="text-[10px] text-slate-400 mt-1 text-center">consecutive days</p>
                </div>

                <div>
                  <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-1.5">
                    Min Severity
                  </label>
                  <ThemedSelect
                    value={formData.minAlertSeverity}
                    onChange={(e) => handleInputChange('minAlertSeverity', String(e.target.value))}
                  >
                    <option value="warning">Warning</option>
                    <option value="critical">Critical</option>
                    <option value="armageddon">Armageddon</option>
                  </ThemedSelect>
                  <p className="text-[10px] text-slate-400 mt-1">to count as breach</p>
                </div>
              </div>
              <p className="text-[10px] text-slate-500 mt-3">
                Email goes out only after the rule has been at or above min severity for{' '}
                {formData.streakDaysToAlert || 2} day(s) in a row. Re-running the same day
                won&apos;t resend.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {isEditing ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  {isEditing ? 'Save Changes' : 'Create Rule'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ErrorRuleModal;
